import React from "react";
import {
  Activity, Gauge, History, BarChart3, Upload, Check, Mail, Eye, Trash2, X,
  Send, RotateCcw, Search, User,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { SectionCard, StatCard, EmptyState, ActionButton } from "./AdminUI";

const DynoTab = ({ ctx, DynoChart, DynoCompareChart }) => {
  const {
    dynoForm, setDynoForm, dynoFileInputRef, dynoFileUploaded, dynoLoading,
    dynoDataPoints, dynoPeaks, handleDynoFileUpload, handleCreateDynoRun, resetDynoForm,
    dynoRuns, dynoCompareSelection, setDynoCompareSelection, dynoCompareModal, setDynoCompareModal,
    selectedDynoRun, setSelectedDynoRun, dynoEmailModal, setDynoEmailModal,
    dynoEmailForm, setDynoEmailForm, handleSendDynoEmail, handleDeleteDynoRun,
    dynoChartRef, dynoCompareChartRef,
    clients, clientSearch, setClientSearch, selectClientForDyno,
  } = ctx;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Gauge size={20} className="text-[#FF3B30]" /> Banc de puissance
          </h2>
          <p className="text-white/50 text-xs mt-0.5">Importez un fichier .txt et visualisez le passage</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={History} label="Total passages" value={dynoRuns.length} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={Activity} label="Puissance max (cv)" value={dynoRuns.length > 0 ? Math.max(...dynoRuns.map((r) => r.max_power || 0)).toFixed(0) : "—"} color="text-orange-400" accent="bg-orange-500/10" />
        <StatCard icon={Gauge} label="Couple max (Nm)" value={dynoRuns.length > 0 ? Math.max(...dynoRuns.map((r) => r.max_torque || 0)).toFixed(0) : "—"} color="text-emerald-400" accent="bg-emerald-500/10" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Form */}
        <SectionCard title="Nouveau passage" icon={Activity}>
          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Fichier de données (.txt)</label>
            <input type="file" accept=".txt" ref={dynoFileInputRef} onChange={handleDynoFileUpload} className="hidden" />
            <button
              onClick={() => dynoFileInputRef.current?.click()}
              disabled={dynoLoading}
              className={`w-full py-3 rounded-md border-2 border-dashed transition-all flex items-center justify-center gap-2 text-sm ${
                dynoFileUploaded
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                  : "border-white/[0.1] hover:border-[#FF3B30] text-white/60 hover:text-white"
              }`}
            >
              {dynoLoading ? "Analyse en cours..." : dynoFileUploaded ? (
                <><Check size={15} /> {dynoDataPoints.length} points importés</>
              ) : (
                <><Upload size={15} /> Importer fichier dyno</>
              )}
            </button>
            {dynoFileUploaded && (
              <p className="mt-2 text-xs text-white/50">
                Max: <span className="text-orange-400 font-semibold">{dynoPeaks.max_power.toFixed(1)} cv</span> @ {dynoPeaks.max_power_rpm} rpm
                {" | "}<span className="text-emerald-400 font-semibold">{dynoPeaks.max_torque.toFixed(1)} Nm</span> @ {dynoPeaks.max_torque_rpm} rpm
              </p>
            )}
          </div>

          {/* Vehicle Info */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Marque *</label>
              <Input value={dynoForm.vehicle_brand} onChange={(e) => setDynoForm({ ...dynoForm, vehicle_brand: e.target.value })} placeholder="Ex: Audi" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Modèle *</label>
              <Input value={dynoForm.vehicle_model} onChange={(e) => setDynoForm({ ...dynoForm, vehicle_model: e.target.value })} placeholder="Ex: S3" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Année</label>
              <Input value={dynoForm.vehicle_year} onChange={(e) => setDynoForm({ ...dynoForm, vehicle_year: e.target.value })} placeholder="Ex: 2011" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Plaque</label>
              <Input value={dynoForm.vehicle_plate} onChange={(e) => setDynoForm({ ...dynoForm, vehicle_plate: e.target.value })} placeholder="Ex: AB-123-CD" className="bg-[#1C1C1E] border-white/[0.06] text-[#FF3B30] font-bold h-9 text-sm" />
            </div>
          </div>

          {/* Config */}
          <div className="mb-4">
            <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Configuration</label>
            <Input value={dynoForm.config_name} onChange={(e) => setDynoForm({ ...dynoForm, config_name: e.target.value })} placeholder="Ex: Stage 3 Bi Injection" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
          </div>

          {/* Atmospheric */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Temp. (°C)</label>
              <Input type="number" value={dynoForm.temperature} onChange={(e) => setDynoForm({ ...dynoForm, temperature: parseFloat(e.target.value) || 0 })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Press. (hPa)</label>
              <Input type="number" value={dynoForm.pressure} onChange={(e) => setDynoForm({ ...dynoForm, pressure: parseFloat(e.target.value) || 0 })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
            <div>
              <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Humidité (%)</label>
              <Input type="number" value={dynoForm.humidity} onChange={(e) => setDynoForm({ ...dynoForm, humidity: parseFloat(e.target.value) || 0 })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
            </div>
          </div>

          {/* Date */}
          <div className="mb-4">
            <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Date du passage</label>
            <Input type="date" value={dynoForm.run_date} onChange={(e) => setDynoForm({ ...dynoForm, run_date: e.target.value })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
          </div>

          {/* Client */}
          <div className="mb-4 p-3 bg-[#1C1C1E] rounded-md border border-white/[0.06]">
            <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1"><User size={10} /> Associer à un client</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <Input
                placeholder="Rechercher un client..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className="bg-[#141414] border-white/[0.06] text-white h-8 text-xs pl-8"
              />
            </div>
            {clientSearch && (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                {clients
                  .filter((c) =>
                    c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                    c.client_email.toLowerCase().includes(clientSearch.toLowerCase())
                  )
                  .slice(0, 5)
                  .map((client) => (
                    <button
                      key={client.client_email}
                      onClick={() => { selectClientForDyno(client); setClientSearch(""); }}
                      className="w-full text-left p-2 bg-[#141414] hover:bg-[#FF3B30]/20 rounded text-xs transition-colors"
                    >
                      <span className="font-medium text-white">{client.client_name}</span>
                      <span className="text-white/40 ml-2">{client.client_email}</span>
                    </button>
                  ))}
              </div>
            )}
            {dynoForm.client_email && (
              <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/25 rounded-md text-xs flex items-center justify-between">
                <span><span className="text-emerald-400 font-medium">{dynoForm.client_name}</span><span className="text-white/50 ml-2">{dynoForm.client_email}</span></span>
                <button onClick={() => setDynoForm({ ...dynoForm, client_email: "", client_name: "", client_phone: "" })} className="text-red-400 hover:text-red-300">
                  <X size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Notes</label>
            <textarea
              value={dynoForm.notes} onChange={(e) => setDynoForm({ ...dynoForm, notes: e.target.value })}
              placeholder="Notes sur le passage..." rows={3}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-sm p-2.5 rounded-md resize-none focus:outline-none focus:border-white/20"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleCreateDynoRun}
              disabled={dynoLoading || !dynoFileUploaded || !dynoForm.vehicle_brand || !dynoForm.vehicle_model}
              className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/30 text-white font-semibold py-2.5 rounded-md text-sm transition-colors"
            >
              Enregistrer le passage
            </button>
            <button
              onClick={resetDynoForm}
              className="px-4 bg-white/[0.06] hover:bg-white/[0.1] text-white rounded-md transition-colors"
              title="Réinitialiser"
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </SectionCard>

        {/* Preview */}
        <SectionCard title="Aperçu graphique" icon={Gauge}>
          {dynoDataPoints.length > 0 ? (
            <DynoChart
              ref={dynoChartRef}
              dataPoints={dynoDataPoints}
              peaks={dynoPeaks}
              vehicleInfo={{
                brand: dynoForm.vehicle_brand || "Marque",
                model: dynoForm.vehicle_model || "Modèle",
                year: dynoForm.vehicle_year || "",
                config: dynoForm.config_name || "",
              }}
              conditions={{
                temperature: dynoForm.temperature,
                pressure: dynoForm.pressure,
                humidity: dynoForm.humidity,
                date: dynoForm.run_date,
              }}
            />
          ) : (
            <div className="h-[400px] flex items-center justify-center border border-dashed border-white/[0.1] rounded-md">
              <div className="text-center">
                <Activity size={40} className="mx-auto mb-3 text-white/20" />
                <p className="text-white/40 text-sm">Importez un fichier pour voir le graphique</p>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* History */}
      <SectionCard
        title={`Historique des passages (${dynoRuns.length})`}
        icon={History}
        action={dynoCompareSelection.length >= 2 && (
          <button
            onClick={() => setDynoCompareModal(true)}
            className="inline-flex items-center gap-1.5 bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
          >
            <BarChart3 size={14} /> Comparer ({dynoCompareSelection.length})
          </button>
        )}
      >
        {dynoRuns.length > 1 && dynoCompareSelection.length < 2 && (
          <div className="mb-3 p-2.5 bg-purple-500/5 border border-purple-500/20 rounded-md text-xs text-purple-400">
            Astuce : sélectionnez 2 à 5 passages pour les comparer sur un même graphique A4.
          </div>
        )}

        {dynoRuns.length === 0 ? (
          <EmptyState icon={History} title="Aucun passage enregistré" />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left">
              <thead className="border-b border-white/[0.06]">
                <tr className="text-white/40 text-[11px] uppercase tracking-wider">
                  <th className="py-3 pr-3 w-10">
                    <input
                      type="checkbox"
                      checked={dynoCompareSelection.length === dynoRuns.length && dynoRuns.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) setDynoCompareSelection(dynoRuns.slice(0, 5).map((r) => r.id));
                        else setDynoCompareSelection([]);
                      }}
                      className="w-3.5 h-3.5 accent-purple-500"
                    />
                  </th>
                  <th className="py-3 pr-3">Date</th>
                  <th className="py-3 pr-3">Véhicule</th>
                  <th className="py-3 pr-3">Config</th>
                  <th className="py-3 pr-3">Puissance</th>
                  <th className="py-3 pr-3">Couple</th>
                  <th className="py-3 pr-3">Client</th>
                  <th className="py-3 pr-0 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {dynoRuns.map((run) => {
                  const selected = dynoCompareSelection.includes(run.id);
                  return (
                    <tr key={run.id} className={`border-b border-white/[0.04] transition-colors ${selected ? "bg-purple-500/[0.08]" : "hover:bg-white/[0.02]"}`}>
                      <td className="py-3 pr-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (dynoCompareSelection.length < 5) setDynoCompareSelection([...dynoCompareSelection, run.id]);
                            } else {
                              setDynoCompareSelection(dynoCompareSelection.filter((id) => id !== run.id));
                            }
                          }}
                          className="w-3.5 h-3.5 accent-purple-500"
                        />
                      </td>
                      <td className="py-3 pr-3 text-white/70 text-xs">{run.run_date || "N/A"}</td>
                      <td className="py-3 pr-3 text-white text-sm font-medium">
                        {run.vehicle_brand} {run.vehicle_model} {run.vehicle_year && <span className="text-white/40 text-xs">({run.vehicle_year})</span>}
                      </td>
                      <td className="py-3 pr-3 text-white/60 text-xs">{run.config_name || "—"}</td>
                      <td className="py-3 pr-3 text-sm">
                        <span className="text-orange-400 font-bold">{run.max_power?.toFixed(1)}</span>
                        <span className="text-white/40 text-[11px] ml-1">cv @ {run.max_power_rpm}</span>
                      </td>
                      <td className="py-3 pr-3 text-sm">
                        <span className="text-emerald-400 font-bold">{run.max_torque?.toFixed(1)}</span>
                        <span className="text-white/40 text-[11px] ml-1">Nm @ {run.max_torque_rpm}</span>
                      </td>
                      <td className="py-3 pr-3 text-white/60 text-xs">{run.client_name || "—"}</td>
                      <td className="py-3 pr-0">
                        <div className="flex gap-1.5 justify-end">
                          <ActionButton icon={Eye} label="" color="blue" onClick={() => setSelectedDynoRun(run)} title="Voir" />
                          <ActionButton icon={Mail} label="" color="green"
                            onClick={() => {
                              setDynoEmailModal(run);
                              setDynoEmailForm({
                                to_email: run.client_email || "",
                                to_name: run.client_name || "",
                                subject: `Résultats Banc de Puissance - ${run.vehicle_brand} ${run.vehicle_model}`,
                                message: "",
                              });
                            }}
                            title="Email"
                          />
                          <ActionButton icon={Trash2} label="" color="red" onClick={() => handleDeleteDynoRun(run.id)} title="Supprimer" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* View Modal */}
      {selectedDynoRun && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#141414]/95 backdrop-blur">
              <h3 className="font-semibold text-white">
                {selectedDynoRun.vehicle_brand} {selectedDynoRun.vehicle_model} — {selectedDynoRun.run_date}
              </h3>
              <button onClick={() => setSelectedDynoRun(null)} className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>
            <div className="p-5">
              <DynoChart
                dataPoints={selectedDynoRun.data_points}
                peaks={{
                  max_power: selectedDynoRun.max_power,
                  max_power_rpm: selectedDynoRun.max_power_rpm,
                  max_torque: selectedDynoRun.max_torque,
                  max_torque_rpm: selectedDynoRun.max_torque_rpm,
                }}
                vehicleInfo={{ brand: selectedDynoRun.vehicle_brand, model: selectedDynoRun.vehicle_model, year: selectedDynoRun.vehicle_year, config: selectedDynoRun.config_name }}
                conditions={{ temperature: selectedDynoRun.temperature, pressure: selectedDynoRun.pressure, humidity: selectedDynoRun.humidity, date: selectedDynoRun.run_date }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {dynoEmailModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Mail size={16} className="text-emerald-400" /> Envoyer les résultats
              </h3>
              <button onClick={() => setDynoEmailModal(null)} className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Email destinataire *</label>
                <Input type="email" value={dynoEmailForm.to_email} onChange={(e) => setDynoEmailForm({ ...dynoEmailForm, to_email: e.target.value })} placeholder="client@example.com" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div>
                <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Nom</label>
                <Input value={dynoEmailForm.to_name} onChange={(e) => setDynoEmailForm({ ...dynoEmailForm, to_name: e.target.value })} placeholder="Nom du destinataire" className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div>
                <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Sujet *</label>
                <Input value={dynoEmailForm.subject} onChange={(e) => setDynoEmailForm({ ...dynoEmailForm, subject: e.target.value })} className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm" />
              </div>
              <div>
                <label className="block text-white/50 text-[11px] uppercase tracking-wider mb-1.5">Message personnalisé</label>
                <textarea
                  value={dynoEmailForm.message} onChange={(e) => setDynoEmailForm({ ...dynoEmailForm, message: e.target.value })}
                  placeholder="Message additionnel..." rows={4}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] text-white text-sm p-2.5 rounded-md resize-none focus:outline-none focus:border-white/20"
                />
              </div>
            </div>
            <div className="p-4 border-t border-white/[0.06] flex gap-2">
              <button onClick={() => setDynoEmailModal(null)} className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors">Annuler</button>
              <button
                onClick={handleSendDynoEmail}
                disabled={dynoLoading || !dynoEmailForm.to_email || !dynoEmailForm.subject}
                className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/30 text-white font-semibold py-2 rounded-md text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Send size={14} /> {dynoLoading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {dynoCompareModal && dynoCompareSelection.length >= 2 && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#141414] border border-white/10 rounded-xl max-w-6xl w-full my-8">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <BarChart3 size={18} className="text-purple-400" />
                Comparaison ({dynoCompareSelection.length} passages)
              </h3>
              <button onClick={() => { setDynoCompareModal(false); setDynoCompareSelection([]); }} className="text-white/60 hover:text-white">
                <X size={22} />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06] text-white/40 text-[11px] uppercase tracking-wider">
                      <th className="text-left py-2 px-3">#</th>
                      <th className="text-left py-2 px-3">Config</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-left py-2 px-3">Puissance</th>
                      <th className="text-left py-2 px-3">Couple</th>
                      <th className="text-left py-2 px-3">Gain</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dynoCompareSelection.map((id, idx) => {
                      const run = dynoRuns.find((r) => r.id === id);
                      const prevRun = idx > 0 ? dynoRuns.find((r) => r.id === dynoCompareSelection[idx - 1]) : null;
                      const powerGain = prevRun && run ? run.max_power - prevRun.max_power : 0;
                      const torqueGain = prevRun && run ? run.max_torque - prevRun.max_torque : 0;
                      if (!run) return null;
                      return (
                        <tr key={run.id} className="border-b border-white/[0.04]">
                          <td className="py-2 px-3 font-bold" style={{ color: ["#FF6B35", "#E91E63", "#9C27B0", "#673AB7", "#FF5722"][idx] }}>{idx + 1}</td>
                          <td className="py-2 px-3 text-white font-medium">{run.config_name || `Passage ${idx + 1}`}</td>
                          <td className="py-2 px-3 text-white/60">{run.run_date}</td>
                          <td className="py-2 px-3"><span className="text-orange-400 font-bold">{run.max_power?.toFixed(0)} ch</span></td>
                          <td className="py-2 px-3"><span className="text-emerald-400 font-bold">{run.max_torque?.toFixed(0)} Nm</span></td>
                          <td className="py-2 px-3">
                            {idx > 0 && (
                              <span className={powerGain >= 0 ? "text-emerald-400" : "text-red-400"}>
                                {powerGain >= 0 ? "+" : ""}{powerGain.toFixed(0)} ch / {torqueGain >= 0 ? "+" : ""}{torqueGain.toFixed(0)} Nm
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <DynoCompareChart
                ref={dynoCompareChartRef}
                runs={dynoCompareSelection.map((id) => dynoRuns.find((r) => r.id === id)).filter(Boolean)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynoTab;
